/**
 * placeVoxelAt — T-1.3.6.
 *
 * Story 1.3 收尾 helper: shared between the ShapePanel drop handler
 * (T-1.3.5) and the new click-to-place handler (T-1.3.6). Centralises
 * the "create a PlaceVoxel command, or signal a conflict" decision in
 * one pure function so both call paths apply identical policy.
 *
 * Contract:
 *   - `placeVoxelAt(coord, voxels)` reads `voxels` to detect conflicts.
 *     On conflict (coord already present) it returns `null`.
 *   - On success it returns a fully-initialised `PlaceVoxel` command
 *     carrying fresh `props` (UUID id, default colour, label=null).
 *   - The function NEVER writes to the input `voxels` Map. The caller
 *     decides when to apply the command (via pushUndo + setVoxels on
 *     the live store, per Constitution §2.1).
 *
 * Why a pure function (not a method on the store):
 *   - Pure = unit-testable on a plain Map-shaped stub. See
 *     `tests/unit/place-voxel-at.test.ts`.
 *   - Pure = no React, no Three.js, no scene store import. Toolbar /
 *     click handler / drop handler all call it the same way.
 *
 * Why check the conflict here (and not only at the render layer):
 *   - T-1.3.4 introduced `checkConflict` as a *view-layer hint* (ghost
 *     colour). T-1.3.5 made PlaceVoxel the source of truth and let the
 *     drop overwrite on red. T-1.3.6 tightens the policy: both click
 *     and drop go through this helper, and the helper returns null on
 *     conflict. The drop UX can still surface the red ghost during the
 *     drag — by the time the drop fires, the helper guarantees we don't
 *     create a "place on occupied" command silently.
 *
 * Out of scope:
 *   - Mesh sync (M3 InstancedMesh).
 *   - DeleteVoxel / MoveVoxel / PaintVoxel (M2).
 *   - Tool-aware policy (M2 will gate this on `state.ui.tool`).
 */

import { PlaceVoxel } from './PlaceVoxel';
import { coordKey } from '../utils/coords';
import type { FaceName, VoxelCoord, VoxelProps } from '../state/types';

export function placeVoxelAt(
  coord: VoxelCoord,
  voxels: Map<VoxelCoord, VoxelProps>,
): PlaceVoxel | null {
  // Map.has() on an array key uses SameValueZero (reference identity),
  // not structural equality. Walk the map looking for a tuple-equal key.
  // The voxel count in M1 stays tiny (< few hundred), so an O(n) scan is
  // fine. M3 may add a side-index keyed by coordKey() if n grows.
  for (const existing of voxels.keys()) {
    if (coordKey(existing) === coordKey(coord)) {
      return null;
    }
  }

  // SPEC §2.1 defaults — keep in lock-step with MainCanvas.makeVoxelProps
  // and the unit test for the same contract. faceColors match `color`
  // for every face; label starts null (LabelNumber lands in M2).
  const faceColors: Record<FaceName, string> = {
    px: '#4A90E2',
    nx: '#4A90E2',
    py: '#4A90E2',
    ny: '#4A90E2',
    pz: '#4A90E2',
    nz: '#4A90E2',
  };
  // UUID strategy mirrors MainCanvas.makeVoxelProps:
  //   - prefer crypto.randomUUID() (modern browsers + jsdom ≥22).
  //   - fall back to a timestamp + random suffix for older runtimes
  //     (paranoid; not currently needed).
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

  const props: VoxelProps = {
    id,
    color: '#4A90E2',
    faceColors,
    label: null,
  };

  return new PlaceVoxel(coord, props);
}
